import { useState, useEffect, useCallback } from "react";
import { X, Save } from "lucide-react";
import { Button, Input, Textarea } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { SettingsSection } from "./SettingsSection";

import {
  useWorkspaceAiPreferences,
  useUpdateWorkspaceAiPreferences,
} from "@/hooks/use-workspace-ai-preferences";
import { useWorkspace } from "@/routes/$workspaceSlug/route";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const LANGUAGE_OPTIONS = ["English", "German", "French", "Spanish"] as const;

const FRAMEWORK_OPTIONS = [
  "ISO 27001",
  "SOC 2",
  "GDPR",
  "HIPAA",
  "PCI DSS",
] as const;

const TONE_OPTIONS = [
  {
    value: "formal" as const,
    labelKey: "settings:aiPreferences.tone.options.formal",
    descKey: "settings:aiPreferences.tone.options.formalDesc",
  },
  {
    value: "conversational" as const,
    labelKey: "settings:aiPreferences.tone.options.conversational",
    descKey: "settings:aiPreferences.tone.options.conversationalDesc",
  },
  {
    value: "technical" as const,
    labelKey: "settings:aiPreferences.tone.options.technical",
    descKey: "settings:aiPreferences.tone.options.technicalDesc",
  },
];

const DETAIL_OPTIONS = [
  {
    value: "concise" as const,
    labelKey: "settings:aiPreferences.detailLevel.options.concise",
    descKey: "settings:aiPreferences.detailLevel.options.conciseDesc",
  },
  {
    value: "standard" as const,
    labelKey: "settings:aiPreferences.detailLevel.options.standard",
    descKey: "settings:aiPreferences.detailLevel.options.standardDesc",
  },
  {
    value: "detailed" as const,
    labelKey: "settings:aiPreferences.detailLevel.options.detailed",
    descKey: "settings:aiPreferences.detailLevel.options.detailedDesc",
  },
];

type Tone = "formal" | "conversational" | "technical";
type DetailLevel = "concise" | "standard" | "detailed";

const TOGGLE_OPTIONS = [
  {
    key: "includeRiskRatings",
    labelKey: "settings:aiPreferences.content.includeRiskRatings",
    descKey: "settings:aiPreferences.content.includeRiskRatingsDesc",
  },
  {
    key: "referenceClauseNumbers",
    labelKey: "settings:aiPreferences.content.referenceClauseNumbers",
    descKey: "settings:aiPreferences.content.referenceClauseNumbersDesc",
  },
  {
    key: "suggestRemediation",
    labelKey: "settings:aiPreferences.content.suggestRemediation",
    descKey: "settings:aiPreferences.content.suggestRemediationDesc",
  },
  {
    key: "includeEvidenceRequirements",
    labelKey: "settings:aiPreferences.content.includeEvidenceRequirements",
    descKey: "settings:aiPreferences.content.includeEvidenceRequirementsDesc",
  },
  {
    key: "crossReferenceControls",
    labelKey: "settings:aiPreferences.content.crossReferenceControls",
    descKey: "settings:aiPreferences.content.crossReferenceControlsDesc",
  },
] as const;

const MAX_CUSTOM_INSTRUCTIONS = 500;

/* ------------------------------------------------------------------ */
/* Toggle Switch                                                       */
/* ------------------------------------------------------------------ */

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
        checked ? "bg-accent" : "bg-border"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Radio Option Group                                                  */
/* ------------------------------------------------------------------ */

function RadioGroup<T extends string>({
  value,
  onChange,
  options,
  t,
}: {
  value: T | undefined;
  onChange: (val: T) => void;
  options: { value: T; labelKey: string; descKey: string }[];
  t: (key: string) => string;
}) {
  return (
    <div className="space-y-2">
      {options.map((opt) => {
        const isSelected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`w-full text-left px-3.5 py-2.5 rounded-lg border transition-colors cursor-pointer ${
              isSelected
                ? "border-accent bg-accent/5"
                : "border-border bg-card hover:bg-card-hover"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  isSelected ? "border-accent" : "border-border"
                }`}
              >
                {isSelected && (
                  <div className="w-2 h-2 rounded-full bg-accent" />
                )}
              </div>
              <div>
                <span
                  className={`text-[13px] font-medium ${isSelected ? "text-primary" : "text-secondary"}`}
                >
                  {t(opt.labelKey)}
                </span>
                <p className="text-xs text-muted mt-0.5">{t(opt.descKey)}</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Language Selector (same as before)                                   */
/* ------------------------------------------------------------------ */

function LanguageSelector({
  value,
  onChange,
  t,
}: {
  value: string;
  onChange: (val: string) => void;
  t: (key: string) => string;
}) {
  const [isCustom, setIsCustom] = useState(
    () =>
      !!value &&
      !LANGUAGE_OPTIONS.includes(value as (typeof LANGUAGE_OPTIONS)[number]),
  );

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-primary block">
        {t("settings:aiPreferences.preferredLanguage.label")}
      </span>
      <p className="text-xs text-secondary">
        {t("settings:aiPreferences.preferredLanguage.description")}
      </p>
      <div className="flex flex-wrap gap-2">
        {LANGUAGE_OPTIONS.map((lang) => (
          <button
            key={lang}
            type="button"
            onClick={() => {
              setIsCustom(false);
              onChange(lang);
            }}
            className={`px-3 py-1.5 text-[13px] rounded-md border transition-colors cursor-pointer ${
              !isCustom && value === lang
                ? "border-accent bg-accent/10 text-accent font-medium"
                : "border-border bg-card text-secondary hover:text-primary hover:bg-card-hover"
            }`}
          >
            {t(
              `settings:aiPreferences.preferredLanguage.options.${lang.toLowerCase()}`,
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setIsCustom(true);
            if (
              LANGUAGE_OPTIONS.includes(
                value as (typeof LANGUAGE_OPTIONS)[number],
              )
            ) {
              onChange("");
            }
          }}
          className={`px-3 py-1.5 text-[13px] rounded-md border transition-colors cursor-pointer ${
            isCustom
              ? "border-accent bg-accent/10 text-accent font-medium"
              : "border-border bg-card text-secondary hover:text-primary hover:bg-card-hover"
          }`}
        >
          {t("settings:aiPreferences.preferredLanguage.custom")}
        </button>
      </div>
      {isCustom && (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t(
            "settings:aiPreferences.preferredLanguage.placeholder",
          )}
          className="max-w-xs"
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Framework Selector                                                  */
/* ------------------------------------------------------------------ */

type FrameworkEntry = { name: string; customInstructions?: string };

function FrameworkSelector({
  selected,
  onChange,
  t,
}: {
  selected: FrameworkEntry[];
  onChange: (val: FrameworkEntry[]) => void;
  t: (key: string) => string;
}) {
  const [customInput, setCustomInput] = useState("");

  const selectedNames = selected.map((f) => f.name);

  const toggle = (frameworkName: string) => {
    if (selectedNames.includes(frameworkName)) {
      onChange(selected.filter((f) => f.name !== frameworkName));
    } else {
      onChange([...selected, { name: frameworkName }]);
    }
  };

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (trimmed && !selectedNames.includes(trimmed)) {
      onChange([...selected, { name: trimmed }]);
      setCustomInput("");
    }
  };

  const customFrameworks = selected.filter(
    (f) =>
      !FRAMEWORK_OPTIONS.includes(f.name as (typeof FRAMEWORK_OPTIONS)[number]),
  );

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-primary block">
        {t("settings:aiPreferences.industryFrameworks.label")}
      </span>
      <p className="text-xs text-secondary">
        {t("settings:aiPreferences.industryFrameworks.description")}
      </p>
      <div className="flex flex-wrap gap-2">
        {FRAMEWORK_OPTIONS.map((fw) => (
          <button
            key={fw}
            type="button"
            onClick={() => toggle(fw)}
            className={`px-3 py-1.5 text-[13px] rounded-md border transition-colors cursor-pointer ${
              selectedNames.includes(fw)
                ? "border-accent bg-accent/10 text-accent font-medium"
                : "border-border bg-card text-secondary hover:text-primary hover:bg-card-hover"
            }`}
          >
            {fw}
          </button>
        ))}
        {customFrameworks.map((fw) => (
          <button
            key={fw.name}
            type="button"
            onClick={() => toggle(fw.name)}
            className="px-3 py-1.5 text-[13px] rounded-md border border-accent bg-accent/10 text-accent font-medium flex items-center gap-1 cursor-pointer"
          >
            {fw.name}
            <X className="h-3 w-3" />
          </button>
        ))}
      </div>
      <div className="flex gap-2 max-w-sm">
        <Input
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          placeholder={t(
            "settings:aiPreferences.industryFrameworks.placeholder",
          )}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={addCustom}
          disabled={!customInput.trim()}
        >
          {t("settings:aiPreferences.industryFrameworks.addCustom")}
        </Button>
      </div>
      {selected.length > 0 && (
        <div className="space-y-3 pt-2">
          {selected.map((fw) => (
            <div key={fw.name} className="space-y-1">
              <span className="text-xs font-medium text-secondary">
                {fw.name}
              </span>
              <Textarea
                value={fw.customInstructions ?? ""}
                onChange={(e) => {
                  onChange(
                    selected.map((s) =>
                      s.name === fw.name
                        ? {
                            ...s,
                            customInstructions: e.target.value || undefined,
                          }
                        : s,
                    ),
                  );
                }}
                placeholder={t(
                  "settings:aiPreferences.industryFrameworks.customInstructionsPlaceholder",
                )}
                rows={2}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

export function SettingsAiPreferencesSection() {
  const { t } = useTranslation(["settings"]);
  const { workspace } = useWorkspace();
  const { preferences, isLoading } = useWorkspaceAiPreferences();
  const { savePreferences, isPending } = useUpdateWorkspaceAiPreferences();

  // Form state
  const [preferredLanguage, setPreferredLanguage] = useState("");
  const [tone, setTone] = useState<Tone | undefined>(undefined);
  const [detailLevel, setDetailLevel] = useState<DetailLevel | undefined>(
    undefined,
  );
  const [toggles, setToggles] = useState<Record<string, boolean>>({
    includeRiskRatings: false,
    referenceClauseNumbers: false,
    suggestRemediation: false,
    includeEvidenceRequirements: false,
    crossReferenceControls: false,
  });
  const [industryFrameworks, setIndustryFrameworks] = useState<
    { name: string; customInstructions?: string }[]
  >([]);
  const [customInstructions, setCustomInstructions] = useState("");
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize form from loaded preferences
  useEffect(() => {
    if (preferences && !hasInitialized) {
      setPreferredLanguage(preferences.preferredLanguage ?? "");
      setTone(preferences.tone);
      setDetailLevel(preferences.detailLevel);
      setToggles({
        includeRiskRatings: preferences.includeRiskRatings ?? false,
        referenceClauseNumbers: preferences.referenceClauseNumbers ?? false,
        suggestRemediation: preferences.suggestRemediation ?? false,
        includeEvidenceRequirements:
          preferences.includeEvidenceRequirements ?? false,
        crossReferenceControls: preferences.crossReferenceControls ?? false,
      });
      setIndustryFrameworks(preferences.industryFrameworks);
      setCustomInstructions(preferences.customInstructions ?? "");
      setHasInitialized(true);
    } else if (preferences === null && !isLoading && !hasInitialized) {
      setHasInitialized(true);
    }
  }, [preferences, isLoading, hasInitialized]);

  const setToggle = useCallback((key: string, value: boolean) => {
    setToggles((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    await savePreferences({
      workspaceId: workspace._id,
      preferredLanguage: preferredLanguage || undefined,
      tone,
      detailLevel,
      includeRiskRatings: toggles.includeRiskRatings,
      referenceClauseNumbers: toggles.referenceClauseNumbers,
      suggestRemediation: toggles.suggestRemediation,
      includeEvidenceRequirements: toggles.includeEvidenceRequirements,
      crossReferenceControls: toggles.crossReferenceControls,
      industryFrameworks,
      customInstructions: customInstructions || undefined,
    });
  }, [
    savePreferences,
    workspace._id,
    preferredLanguage,
    tone,
    detailLevel,
    toggles,
    industryFrameworks,
    customInstructions,
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-muted">
          {t("settings:aiPreferences.loading")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[20px] font-semibold text-primary">
            {t("settings:aiPreferences.title")}
          </h2>
          <p className="text-sm text-secondary mt-1">
            {t("settings:aiPreferences.description")}
          </p>
        </div>
        <Button
          variant="primary"
          icon={Save}
          onClick={handleSave}
          loading={isPending}
        >
          {t("settings:aiPreferences.save")}
        </Button>
      </div>

      <SettingsSection title={t("settings:aiPreferences.cards.language")}>
        <LanguageSelector
          value={preferredLanguage}
          onChange={setPreferredLanguage}
          t={t as (key: string) => string}
        />
      </SettingsSection>

      <SettingsSection title={t("settings:aiPreferences.cards.toneStyle")}>
        <div className="space-y-5">
          <div className="space-y-2">
            <span className="text-sm font-medium text-primary block">
              {t("settings:aiPreferences.tone.label")}
            </span>
            <RadioGroup
              value={tone}
              onChange={setTone}
              options={TONE_OPTIONS}
              t={t as (key: string) => string}
            />
          </div>
          <div className="space-y-2">
            <span className="text-sm font-medium text-primary block">
              {t("settings:aiPreferences.detailLevel.label")}
            </span>
            <RadioGroup
              value={detailLevel}
              onChange={setDetailLevel}
              options={DETAIL_OPTIONS}
              t={t as (key: string) => string}
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection title={t("settings:aiPreferences.cards.content")}>
        <div className="space-y-5">
          <div className="space-y-3">
            {TOGGLE_OPTIONS.map((opt) => (
              <div
                key={opt.key}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] font-medium text-primary block">
                    {t(opt.labelKey)}
                  </span>
                  <p className="text-xs text-muted mt-0.5">{t(opt.descKey)}</p>
                </div>
                <ToggleSwitch
                  checked={toggles[opt.key] ?? false}
                  onChange={(val) => setToggle(opt.key, val)}
                />
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-border">
            <FrameworkSelector
              selected={industryFrameworks}
              onChange={setIndustryFrameworks}
              t={t as (key: string) => string}
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title={t("settings:aiPreferences.cards.customInstructions")}
      >
        <div className="space-y-2">
          <p className="text-xs text-secondary">
            {t("settings:aiPreferences.customInstructions.description")}
          </p>
          <Textarea
            value={customInstructions}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CUSTOM_INSTRUCTIONS) {
                setCustomInstructions(e.target.value);
              }
            }}
            placeholder={t(
              "settings:aiPreferences.customInstructions.placeholder",
            )}
            rows={3}
            className="max-w-xl"
          />
          <p className="text-xs text-muted text-right max-w-xl">
            {customInstructions.length}/{MAX_CUSTOM_INSTRUCTIONS}
          </p>
        </div>
      </SettingsSection>
    </div>
  );
}
